/**
 * @file     ListingSpecsGrid.tsx
 * @purpose  Listing facts as one two-column hairline FactList (size, money,
 *           building and location facts, the details strip and, on request,
 *           the additional MLS facts), then highlights and amenities as
 *           pills and the labelled inclusion lists as a second FactList.
 *           Every row and pill carries its ListingIcons glyph beside the
 *           label. Field derivation stays in ./listingSpecs. Without VOW
 *           access the VOW-only rows draw as blurred bars (VowLockedFacts);
 *           each bar's row is one button into the VOW gate, with the
 *           section's call to action under the lists.
 */
'use client';

import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import type { ListingDetail } from '@/services/mls/mlsPropertyService';
import type { ListingSurface } from '@/core/analytics/listingEvents';
import { FactList, type FactItem } from '@/components/hub/FactList';
import { microLabelSx } from '@/components/hub/hubKitStyles';
import { ListingIcon, factIconName, featureGroupIconName, specIconName, type ListingIconName } from '@/components/icons/ListingIcons';
import { FeaturePills } from './PropertyHighlights';
import {
  buildPropertyFacts,
  buildLocationFacts,
  buildDetailStrip,
  buildFeatureGroups,
  resolveAmenities,
} from './listingSpecs';
import { VowDeepDiveFacts, VowLockedValue, VowUnlockCta, useVowTeaserGate, type VowTeaserRow } from './VowLockedFacts';

interface Props {
  /**
   * The full listing row. Passed whole (not threaded as scalar props) so any
   * field modeled on ListingDetail is reachable here by construction.
   */
  listing: ListingDetail;
  /** Fold AdditionalDetails (common interest, levels, entry, main floor, tax year) into the list. */
  includeAdditional?: boolean;
  /**
   * Draw the VOW rooms, finished areas, systems and lot rows here. Off on a
   * listing page whose Space section already carries them.
   */
  showDeepDive?: boolean;
  /** Server-known state for the VOW call to action; the client session otherwise. */
  vowCta?: { isAuthenticated?: boolean; needsReactivation?: boolean };
  /** Where the facts render, for the gate event. */
  surface?: ListingSurface;
}

// Card-era labels read as headings; the fact list reads as sentences.
const FACT_LABEL: Record<string, string> = {
  Beds: 'Bedrooms',
  Baths: 'Bathrooms',
  'Floor area': 'Interior',
  'Gross tax': 'Property tax',
  Type: 'Property type',
};

export function additionalFacts(listing: ListingDetail): FactItem[] {
  return [
    { label: 'Common interest', value: listing.commonInterest },
    { label: 'Levels', value: listing.levels && listing.levels.length > 0 ? listing.levels.join(', ') : null },
    { label: 'Entry', value: listing.entryLocation },
    {
      label: 'Main floor',
      value: listing.mainFloorArea && listing.mainFloorArea > 0 ? `${listing.mainFloorArea.toLocaleString('en-CA')} sq ft` : null,
    },
    { label: 'Tax year', value: listing.taxYear ? String(listing.taxYear) : null },
  ];
}

export function listingFacts(listing: ListingDetail, includeAdditional = false, teaser?: VowTeaserRow): FactItem[] {
  const seen = new Set<string>();
  const items: FactItem[] = [];
  const push = (label: string, value: string | null | undefined, icon: ListingIconName | null) => {
    const shown = FACT_LABEL[label] ?? label;
    if (seen.has(shown) || value == null || value === '') return;
    seen.add(shown);
    items.push({
      label: shown,
      value: value.replace(/ft²/g, 'sq ft'),
      icon: icon ? <ListingIcon name={icon} size={18} /> : undefined,
    });
  };
  for (const s of buildPropertyFacts(listing)) push(s.label, s.value, specIconName(s.iconKey));
  for (const s of buildLocationFacts(listing)) push(s.label, s.value, specIconName(s.iconKey));
  for (const d of buildDetailStrip(listing)) {
    if (d.locked) {
      if (seen.has(d.label)) continue;
      seen.add(d.label);
      const icon = factIconName(d.label);
      items.push({
        label: d.label,
        value: <VowLockedValue width="short" />,
        icon: icon ? <ListingIcon name={icon} size={18} /> : undefined,
        ...(d.field ? teaser?.(d.field, d.label) : null),
      });
    } else {
      push(d.label, d.value, factIconName(d.label));
    }
  }
  if (includeAdditional) {
    for (const a of additionalFacts(listing)) push(a.label, typeof a.value === 'string' ? a.value : null, factIconName(a.label));
  }
  return items;
}

/** True when any row this grid draws is a VOW teaser. */
export function hasVowLockedRows(listing: ListingDetail, showDeepDive: boolean): boolean {
  const present = listing.vowFieldsPresent ?? [];
  if (present.length === 0) return false;
  return showDeepDive || present.some((f) => (VOW_INLINE_FIELDS as readonly string[]).includes(f));
}

const VOW_INLINE_FIELDS = ['listedDate', 'parcelNumber', 'flooring', 'constructionMaterials', 'roof'] as const;

export function ListingSpecsGrid({ listing, includeAdditional = false, showDeepDive = true, vowCta, surface = 'page' }: Props) {
  const { t } = useTranslation();
  const gate = useVowTeaserGate({
    isAuthenticated: vowCta?.isAuthenticated,
    needsReactivation: vowCta?.needsReactivation,
    surface,
    listingKey: listing.listingKey,
  });
  const facts = listingFacts(listing, includeAdditional, gate.teaserRow);
  const locked = hasVowLockedRows(listing, showDeepDive);
  const amenities = resolveAmenities(listing.associationAmenities, listing.extractedFeatures);
  const pills = [
    ...listing.extractedFeatures.map((label) => ({ label })),
    ...amenities.map((a) => ({ label: a.label, icon: specIconName(a.iconKey) })),
  ];
  const featureGroups = buildFeatureGroups(listing);

  return (
    <Box>
      <FactList items={facts} ariaLabel={t('listingDetail.facts.label', { defaultValue: 'Home facts' })} />

      {pills.length > 0 ? (
        <Box sx={{ mt: 6 }}>
          <Box component="h3" sx={{ ...microLabelSx, mb: 3 }}>
            {t('listingDetail.facts.highlights', { defaultValue: 'Highlights' })}
          </Box>
          <FeaturePills items={pills} />
        </Box>
      ) : null}

      {featureGroups.length > 0 ? (
        <Box sx={{ mt: 6 }}>
          <Box component="h3" sx={{ ...microLabelSx, mb: 2 }}>
            {t('listingDetail.facts.inclusions', { defaultValue: 'Features and inclusions' })}
          </Box>
          <FactList
            columns={1}
            items={featureGroups.map((g) => ({
              label: g.label,
              value: g.locked ? <VowLockedValue width="medium" /> : g.values.join(', '),
              icon: <ListingIcon name={featureGroupIconName(g.label)} size={18} />,
              ...(g.locked && g.field ? gate.teaserRow(g.field, g.label) : null),
            }))}
          />
        </Box>
      ) : null}

      {showDeepDive ? <VowDeepDiveFacts listing={listing} teaser={gate.teaserRow} /> : null}

      {locked ? <VowUnlockCta gate={gate} /> : null}
      {/* Stays mounted after the unlock so the modal can finish its own flow. */}
      {gate.modal}
    </Box>
  );
}
